import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Star, ShareNetwork, Link as LinkIcon, XLogo } from "@phosphor-icons/react";
import { useFavorites } from "../contexts/FavoritesContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import NASCARRacePanel from "../components/NASCARRacePanel";
import NBAGamePanel from "../components/NBAGamePanel";
import NHLGamePanel from "../components/NHLGamePanel";
import SoccerGamePanel from "../components/SoccerGamePanel";
import NFLGamePanel from "../components/NFLGamePanel";
import PGALeaderboard from "../components/PGALeaderboard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BaseDiamond({ onFirst, onSecond, onThird, outs }) {
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid="base-diamond">
      {/* Second base */}
      <div className={`w-4 h-4 sm:w-5 sm:h-5 rotate-45 border-2 ${onSecond ? "bg-[#007AFF] border-[#007AFF]" : "border-white/30 bg-transparent"}`} />
      <div className="flex gap-4 sm:gap-6 -mt-1">
        {/* Third base */}
        <div className={`w-4 h-4 sm:w-5 sm:h-5 rotate-45 border-2 ${onThird ? "bg-[#007AFF] border-[#007AFF]" : "border-white/30 bg-transparent"}`} />
        {/* First base */}
        <div className={`w-4 h-4 sm:w-5 sm:h-5 rotate-45 border-2 ${onFirst ? "bg-[#007AFF] border-[#007AFF]" : "border-white/30 bg-transparent"}`} />
      </div>
      {/* Outs */}
      <div className="flex gap-1 mt-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`w-2 h-2 rounded-full ${i < (outs || 0) ? "bg-[#007AFF]" : "bg-white/20"}`} />
        ))}
      </div>
    </div>
  );
}

function CountDisplay({ label, count, max, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] sm:text-xs text-white/50 font-semibold w-14">{label}</span>
      <div className="flex gap-1.5">
        {[...Array(max)].map((_, i) => (
          <div key={i} className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border-2 ${i < count ? `${color} border-current` : "border-white/20"}`} />
        ))}
      </div>
    </div>
  );
}

export default function GameDetail() {
  const { leagueKey, eventId } = useParams();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchGame = () => {
    if (!leagueKey || !eventId) return;
    axios.get(`${API}/game/${leagueKey}/${eventId}`)
      .then((r) => {
        if (r.data.error) setError(r.data.error);
        else setGame(r.data);
      })
      .catch(() => setError("Failed to load game details"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    fetchGame();
    // Auto-refresh every 15s for live games
    pollRef.current = setInterval(fetchGame, 15000);
    return () => clearInterval(pollRef.current);
  }, [leagueKey, eventId]);

  if (loading) return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-4">
      <div className="h-40 bg-[#141414] rounded-xl animate-pulse" />
      <div className="h-80 bg-[#141414] rounded-xl animate-pulse" />
    </div>
  );

  if (error || !game) return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12 text-center">
      <p className="text-white/40 text-sm">{error || "Game not found"}</p>
      <Link to="/" className="text-[#007AFF] text-sm mt-4 inline-block" data-testid="back-to-dashboard">Back to Dashboard</Link>
    </div>
  );

  const isLive = game.statusState === "in";
  const isMLB = leagueKey === "mlb";
  const isNASCAR = leagueKey === "nascar";
  const isNBA = leagueKey === "nba";
  const isNHL = leagueKey === "nhl";
  const isNFL = leagueKey === "nfl" || leagueKey === "ncaaf";
  const isSoccer = leagueKey === "epl" || leagueKey === "mls";
  const isPGA = leagueKey === "pga";
  const sit = game.situation || {};
  const away = game.awayTeam || {};
  const home = game.homeTeam || {};

  // NASCAR gets its own full-page panel
  if (isNASCAR) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="game-detail">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="game-back-btn">
            <ArrowLeft size={16} className="text-white/70" />
          </Link>
          <span className="text-xs tracking-widest uppercase text-[#007AFF] font-bold">NASCAR</span>
          {isLive && (
            <span className="inline-flex items-center gap-1 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 px-2 py-0.5 rounded text-[10px] tracking-widest uppercase font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-live-pulse" /> LIVE
            </span>
          )}
        </div>
        <NASCARRacePanel />
      </div>
    );
  }

  // PGA Golf gets its own leaderboard
  if (isPGA) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="game-detail">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="game-back-btn">
            <ArrowLeft size={16} className="text-white/70" />
          </Link>
          <span className="text-xs tracking-widest uppercase text-[#007AFF] font-bold">PGA GOLF</span>
        </div>
        <PGALeaderboard />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="game-detail">
      {/* Back nav */}
      <div className="flex items-center gap-3">
        <Link to={leagueKey ? `/league/${leagueKey}` : "/"} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="game-back-btn">
          <ArrowLeft size={16} className="text-white/70" />
        </Link>
        <span className="text-xs tracking-widest uppercase text-[#007AFF] font-bold">{game.leagueName}</span>
        {isLive && (
          <span className="inline-flex items-center gap-1 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 px-2 py-0.5 rounded text-[10px] tracking-widest uppercase font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-live-pulse" /> LIVE
          </span>
        )}
        <button onClick={() => toggleFavorite(eventId)} className="ml-auto p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="game-favorite-btn">
          <Star size={16} weight={isFavorite(eventId) ? "fill" : "regular"} className={isFavorite(eventId) ? "text-[#007AFF]" : "text-white/40"} />
        </button>
        <button
          onClick={() => {
            const url = window.location.href;
            const text = `${away.displayName || away.name} ${away.score} - ${home.displayName || home.name} ${home.score}`;
            window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank");
          }}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          data-testid="share-x-btn"
        >
          <XLogo size={16} className="text-white/40" />
        </button>
        <button
          onClick={() => { navigator.clipboard.writeText(window.location.href); }}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          data-testid="share-link-btn"
        >
          <LinkIcon size={16} className="text-white/40" />
        </button>
      </div>

      {/* ===== SCOREBOARD HEADER ===== */}
      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="game-scoreboard">
        <div className="px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-2">
          {/* Away team */}
          <Link to={`/team/${leagueKey}/${away.id}`} className="flex items-center gap-2 sm:gap-3 group flex-1 min-w-0" data-testid="away-team-link">
            {away.logo && <img src={away.logo} alt="" className="w-10 h-10 sm:w-14 sm:h-14 object-contain group-hover:scale-110 transition-transform" />}
            <div className="min-w-0">
              <p className="text-[10px] text-white/40">{away.record}</p>
              <p className="text-sm sm:text-lg font-bold text-white truncate group-hover:text-[#007AFF] transition-colors">{away.displayName || away.name}</p>
            </div>
          </Link>
          <div className={`score-font text-3xl sm:text-5xl ${away.winner ? "text-white" : "text-white/60"}`} data-testid="away-score">{away.score}</div>

          {/* Center: inning + bases (MLB) or status */}
          <div className="px-2 sm:px-4 text-center flex flex-col items-center gap-1">
            <p className="text-xs sm:text-sm font-bold text-white/80 uppercase tracking-wide">{game.statusDetail || game.statusDesc}</p>
            {isMLB && isLive && (
              <BaseDiamond onFirst={sit.onFirst} onSecond={sit.onSecond} onThird={sit.onThird} outs={sit.outs} />
            )}
          </div>

          <div className={`score-font text-3xl sm:text-5xl ${home.winner ? "text-white" : "text-white/60"}`} data-testid="home-score">{home.score}</div>
          {/* Home team */}
          <Link to={`/team/${leagueKey}/${home.id}`} className="flex items-center gap-2 sm:gap-3 group flex-1 min-w-0 justify-end text-right" data-testid="home-team-link">
            <div className="min-w-0">
              <p className="text-[10px] text-white/40">{home.record}</p>
              <p className="text-sm sm:text-lg font-bold text-white truncate group-hover:text-[#007AFF] transition-colors">{home.displayName || home.name}</p>
            </div>
            {home.logo && <img src={home.logo} alt="" className="w-10 h-10 sm:w-14 sm:h-14 object-contain group-hover:scale-110 transition-transform" />}
          </Link>
        </div>
        {game.venue && (
          <div className="px-4 py-1.5 border-t border-white/5 text-center text-[10px] text-white/30 flex items-center justify-center gap-1">
            <MapPin size={10} /> {game.venue}
          </div>
        )}
      </div>

      {/* ===== TABS: Game / Box Score / Plays ===== */}
      <Tabs defaultValue="game" className="w-full">
        <TabsList className="bg-[#141414] border border-white/10 p-1 rounded-lg">
          <TabsTrigger value="game" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-game">Game</TabsTrigger>
          <TabsTrigger value="boxscore" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-boxscore">Box Score</TabsTrigger>
          <TabsTrigger value="plays" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-plays">Plays</TabsTrigger>
        </TabsList>

        {/* ===== GAME TAB ===== */}
        <TabsContent value="game" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Left: Pitcher/Batter + Count (for MLB) */}
            <div className="lg:col-span-2 space-y-4">
              {isMLB && isLive && (
                <>
                  {/* Pitcher info */}
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="pitcher-info">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-full bg-[#FF3B30]/20 flex items-center justify-center text-[#FF3B30] text-xs font-bold">P</div>
                      <div>
                        <p className="text-sm font-bold text-white">{sit.pitcher || "---"}</p>
                        <p className="text-[10px] text-white/40">Pitching</p>
                      </div>
                    </div>
                    {/* Balls & Strikes */}
                    <div className="space-y-2 mt-3">
                      <CountDisplay label="Balls" count={sit.balls || 0} max={4} color="text-[#34C759]" />
                      <CountDisplay label="Strikes" count={sit.strikes || 0} max={3} color="text-[#FF3B30]" />
                      <CountDisplay label="Outs" count={sit.outs || 0} max={3} color="text-[#007AFF]" />
                    </div>
                  </div>

                  {/* Batter info */}
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="batter-info">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-[#007AFF]/20 flex items-center justify-center text-[#007AFF] text-xs font-bold">AB</div>
                      <div>
                        <p className="text-sm font-bold text-white">{sit.batter || "---"}</p>
                        <p className="text-[10px] text-white/40">At Bat</p>
                      </div>
                    </div>
                  </div>

                  {/* Base runners visual */}
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-4 flex flex-col items-center" data-testid="bases-panel">
                    <p className="text-[10px] tracking-widest uppercase text-white/40 mb-3">Bases</p>
                    <svg viewBox="0 0 100 100" className="w-24 h-24">
                      <line x1="50" y1="15" x2="85" y2="50" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
                      <line x1="85" y1="50" x2="50" y2="85" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
                      <line x1="50" y1="85" x2="15" y2="50" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
                      <line x1="15" y1="50" x2="50" y2="15" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
                      <rect x="44" y="79" width="12" height="12" rx="1" fill="rgba(255,255,255,0.3)" transform="rotate(45 50 85)" />
                      <rect x="44" y="9" width="12" height="12" rx="1" fill={sit.onSecond ? "#007AFF" : "rgba(255,255,255,0.15)"} transform="rotate(45 50 15)" />
                      <rect x="9" y="44" width="12" height="12" rx="1" fill={sit.onThird ? "#007AFF" : "rgba(255,255,255,0.15)"} transform="rotate(45 15 50)" />
                      <rect x="79" y="44" width="12" height="12" rx="1" fill={sit.onFirst ? "#007AFF" : "rgba(255,255,255,0.15)"} transform="rotate(45 85 50)" />
                    </svg>
                    <div className="flex gap-4 mt-2 text-[10px] text-white/30">
                      <span>1B: {sit.onFirst ? "Occupied" : "Empty"}</span>
                      <span>2B: {sit.onSecond ? "Occupied" : "Empty"}</span>
                      <span>3B: {sit.onThird ? "Occupied" : "Empty"}</span>
                    </div>
                  </div>
                </>
              )}

              {/* Non-MLB sport-specific panels */}
              {isNBA && <NBAGamePanel game={game} />}
              {isNHL && <NHLGamePanel game={game} />}
              {isNFL && <NFLGamePanel game={game} />}
              {isSoccer && <SoccerGamePanel game={game} />}

              {/* Fallback for sports without custom panels */}
              {!isMLB && !isNBA && !isNHL && !isNFL && !isSoccer && (
                <div className="bg-[#141414] border border-white/10 rounded-xl p-6 text-center">
                  <p className="text-lg font-bold text-white">{game.statusDetail || game.statusDesc}</p>
                  {game.venue && <p className="text-xs text-white/30 mt-2 flex items-center justify-center gap-1"><MapPin size={12} />{game.venue}</p>}
                </div>
              )}
            </div>

            {/* Right: Recent Plays */}
            <div className="lg:col-span-3">
              <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="recent-plays">
                <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                  <h3 className="font-['Bebas_Neue'] text-lg uppercase tracking-wider text-white">Recent Plays</h3>
                </div>
                <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5">
                  {(game.plays || []).slice().reverse().slice(0, 20).map((play, idx) => (
                    <div key={play.id || idx} className={`px-4 py-3 ${play.scoringPlay ? "bg-[#34C759]/5 border-l-2 border-l-[#34C759]" : ""}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {isMLB && (away.logo || home.logo) && (
                          <img src={play.period % 2 === 0 ? (home.logo || away.logo) : (away.logo || home.logo)} alt="" className="w-4 h-4 object-contain" />
                        )}
                        <span className="text-xs font-bold text-white/70">
                          {isMLB
                            ? `${play.period > 0 ? (play.period % 2 === 1 ? "Top" : "Bottom") + " " : ""}${play.period > 0 ? Math.ceil(play.period / 2) + (["st","nd","rd"][Math.ceil(play.period/2)-1] || "th") : ""}`
                            : play.period > 0 ? `Q${play.period}` : ""
                          }
                        </span>
                        {play.clock && <span className="text-[10px] text-white/30 score-font">{play.clock}</span>}
                        {play.scoringPlay && <span className="text-[10px] text-[#34C759] font-bold tracking-wider uppercase ml-auto">Scoring Play</span>}
                      </div>
                      <p className="text-xs text-white/60 leading-relaxed">{play.text || play.shortText}</p>
                    </div>
                  ))}
                  {(!game.plays || game.plays.length === 0) && (
                    <p className="px-4 py-8 text-center text-xs text-white/30">No plays available yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ===== BOX SCORE TAB ===== */}
        <TabsContent value="boxscore" className="mt-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="box-score">
            <div className="px-4 py-3 border-b border-white/5">
              <h3 className="font-['Bebas_Neue'] text-lg uppercase tracking-wider text-white">Score</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-white/40 uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 w-44">Team</th>
                    {(home.linescores || away.linescores || []).map((_, i) => (
                      <th key={i} className="text-center px-2.5 py-2.5 w-9 font-mono">{i + 1}</th>
                    ))}
                    <th className="text-center px-3 py-2.5 w-10 bg-white/5 font-bold">R</th>
                    {isMLB && <th className="text-center px-3 py-2.5 w-10 font-bold">H</th>}
                    {isMLB && <th className="text-center px-3 py-2.5 w-10 font-bold">E</th>}
                  </tr>
                </thead>
                <tbody>
                  {/* Away */}
                  <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {away.logo && <img src={away.logo} alt="" className="w-5 h-5 object-contain" />}
                        <span className="font-semibold text-white">{away.displayName || away.name}</span>
                      </div>
                    </td>
                    {(away.linescores || []).map((s, i) => (
                      <td key={i} className="text-center px-2.5 py-3 score-font text-white/60">{s}</td>
                    ))}
                    {/* Fill empty innings */}
                    {(away.linescores || []).length < (home.linescores || []).length && [...Array((home.linescores || []).length - (away.linescores || []).length)].map((_, i) => (
                      <td key={`ae${i}`} className="text-center px-2.5 py-3 score-font text-white/20">-</td>
                    ))}
                    <td className="text-center px-3 py-3 score-font font-bold text-white bg-white/5">{away.score}</td>
                    {isMLB && <td className="text-center px-3 py-3 score-font text-white/70">{game.awayHits || "0"}</td>}
                    {isMLB && <td className="text-center px-3 py-3 score-font text-white/70">{game.awayErrors || "0"}</td>}
                  </tr>
                  {/* Home */}
                  <tr className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {home.logo && <img src={home.logo} alt="" className="w-5 h-5 object-contain" />}
                        <span className="font-semibold text-white">{home.displayName || home.name}</span>
                      </div>
                    </td>
                    {(home.linescores || []).map((s, i) => (
                      <td key={i} className="text-center px-2.5 py-3 score-font text-white/60">{s}</td>
                    ))}
                    {(home.linescores || []).length < (away.linescores || []).length && [...Array((away.linescores || []).length - (home.linescores || []).length)].map((_, i) => (
                      <td key={`he${i}`} className="text-center px-2.5 py-3 score-font text-white/20">-</td>
                    ))}
                    <td className="text-center px-3 py-3 score-font font-bold text-white bg-white/5">{home.score}</td>
                    {isMLB && <td className="text-center px-3 py-3 score-font text-white/70">{game.homeHits || "0"}</td>}
                    {isMLB && <td className="text-center px-3 py-3 score-font text-white/70">{game.homeErrors || "0"}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ===== PLAYS TAB ===== */}
        <TabsContent value="plays" className="mt-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="full-plays">
            <div className="px-4 py-3 border-b border-white/5">
              <h3 className="font-['Bebas_Neue'] text-lg uppercase tracking-wider text-white">All Plays</h3>
            </div>
            <div className="max-h-[600px] overflow-y-auto divide-y divide-white/5">
              {(game.plays || []).slice().reverse().map((play, idx) => (
                <div key={play.id || idx} className={`px-4 py-3 ${play.scoringPlay ? "bg-[#34C759]/5 border-l-2 border-l-[#34C759]" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {play.clock && <span className="text-[10px] text-white/30 score-font">{play.clock}</span>}
                    {play.period > 0 && (
                      <span className="text-[10px] text-white/50 font-semibold">
                        {isMLB ? `${play.period % 2 === 1 ? "Top" : "Bot"} ${Math.ceil(play.period / 2)}` : `Q${play.period}`}
                      </span>
                    )}
                    {play.type && <span className="text-[10px] text-white/30">{play.type}</span>}
                    {play.scoringPlay && <span className="text-[10px] text-[#34C759] font-bold tracking-wider uppercase ml-auto">Scoring</span>}
                  </div>
                  <p className="text-xs text-white/70 leading-relaxed">{play.text || play.shortText}</p>
                </div>
              ))}
              {(!game.plays || game.plays.length === 0) && (
                <p className="px-4 py-8 text-center text-xs text-white/30">No plays available</p>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
